import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _partition from 'lodash/partition';
import _maxBy from 'lodash/maxBy';
import _indexOf from 'lodash/indexOf';
import _isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import { saveUploadcareImageToZola } from 'actions/UploadcareActions';
import DragAndDrop from '../../../../components/util/DragAndDrop';

// Components
import DraggableTextLayer from './DraggableTextLayer';
import ImageLayer from './ImageLayer';
import Bleed from './Bleed';
import ElementStyleEditor from '../ElementStyleEditor/ElementStyleEditor';

// Actions
import { actions as projectActions } from '../../../actions/projectActions';

// Styles
import './Preview.less';

// Util
import FeatureFlags from '../../../../util/featureFlags';
import {
  autocropUploadcareFile,
  fetchUploadcareFile,
} from '../../../../components/common/UploadcareWidget/utils';
import {
  getCachedUploadcareFiles,
  getCachedMetadata,
} from '../../../../selectors/uploadcareSelectors';

// Constants
const SHIMMER_TYPES = ['shimmer-silver', 'shimmer-gold'];

// TODO: look into making this a pure component, seems to be dragging a little bit
class Preview extends React.Component {
  constructor(props) {
    super(props);
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleImageReorder = this.handleImageReorder.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  setWrapperRef(node) {
    this.wrapperRef = node;
  }

  handleClickOutside(e) {
    const { activeField, setActiveField } = this.props;
    const deselectActive = window.matchMedia('(min-width: 769px)').matches;

    // check to make sure event target is a child of preview
    // if it is not, we want to set activeField to null
    if (deselectActive && activeField) {
      // if we dont have an activeField, do nothing
      if (this.wrapperRef && !this.wrapperRef.contains(e.target)) {
        // if target is not a child of outermost div, the event originated
        // outside of Preview, set activeField to null
        setActiveField(null);
      } else if (this.cardRef && e.target.isSameNode(this.cardRef)) {
        // if the event originated inside outermost div but not from on of the
        // text element components (meaning it came from this.cardRef) then set activeField
        // to null
        setActiveField(null);
      }
    }
  }

  handleImageReorder(imageElementA, imageElementB) {
    const {
      cachedMetadata,
      cachedUploadcareFiles,
      copyImageToZola,
      onImageDelete,
      onImageSave,
      setActiveField,
    } = this.props;

    // Deselect the active image element
    setActiveField(null);

    // Get each element's cached uploadcare url
    const zolaImageIds = [imageElementA, imageElementB].map(elem => elem.image_id);
    const uploadcareUrls = zolaImageIds.map(id => {
      const metadata = id && cachedMetadata[id];
      return (metadata && metadata.uploadcare_url) || null;
    });

    // For each uploadcare url, load uploadcare file data
    const filePromises = uploadcareUrls.map(uploadcareUrl => {
      if (!uploadcareUrl) return Promise.resolve(null);

      const cachedUploadcareFile = cachedUploadcareFiles[uploadcareUrl];
      if (cachedUploadcareFile) return Promise.resolve(cachedUploadcareFile);

      return fetchUploadcareFile(uploadcareUrl);
    });

    Promise.all(filePromises).then(([fileA, fileB]) => {
      // Autocrop each valid file for the OTHER image element

      // For every valid Uploadcare file, autocrop for the OTHER element and copy to Zola
      const copyPromiseA = fileA
        ? copyImageToZola(autocropUploadcareFile(fileA, imageElementB))
        : Promise.resolve(null);
      const copyPromiseB = fileB
        ? copyImageToZola(autocropUploadcareFile(fileB, imageElementA))
        : Promise.resolve(null);

      // Save the newly saved Zola images to the OTHER element
      Promise.all([copyPromiseA, copyPromiseB]).then(([zolaImageA, zolaImageB]) => {
        const updatedImages = {};

        // Update imageElementA by assigning it the new image OR clearing it (if the new image is null)
        if (zolaImageB) {
          updatedImages[imageElementA.uuid] = zolaImageB.uuid;
        } else {
          onImageDelete(imageElementA.uuid);
        }

        // Update imageElementB by assigning it the new image OR clearing it (if the new image is null)
        if (zolaImageA) {
          updatedImages[imageElementB.uuid] = zolaImageA.uuid;
        } else {
          onImageDelete(imageElementB.uuid);
        }

        // If either new image is defined, save the customization.
        if (Object.keys(updatedImages).length > 0) {
          onImageSave(updatedImages);
        }
      });
    });
  }

  render() {
    /**
     * TODO:
     * Positioning logic for folded cards is determined by isFoldable prop
     * this results in various terniary expresions for positioning see if
     * a separate component is needed to separate this logic
     */
    const {
      activeField,
      activeMenuName,
      cachedMetadata,
      children,
      customizationDeleted,
      customizationValues,
      disableBleed,
      editable,
      elements,
      fonts,
      footerLabel,
      handleChange,
      handleDelete,
      handleTextBlur,
      handleTextChange,
      handleCloneElement,
      hasMultipleCustomizableImages = false,
      imageUploadEnabled,
      infoComponents,
      isFlipped,
      isFoldable,
      isReviewStep,
      onClickAction,
      onImageDelete,
      onImageSave,
      onRemoveElementHeightError,
      onRemoveFieldWidthError,
      setActiveField,
      onSetElementHeightError,
      onSetFieldWidthError,
      onUpdateField,
      page,
      shape,
      enableEnforceCaps,
      projectAvailableOptions,
      showFoilOnThisStep,
      foiledElements,
      customizationCardType,
      customizationFoilColor,
      projectType,
      handleUpdateFoil,
      customizationSize,
      backgroundColor = 'none',
      envelopeType,
    } = this.props;

    const {
      width,
      height,
      vertical_bleed: verticalBleed,
      horizontal_bleed: horizontalBleed,
      tags,
    } = page;

    const finalHeight = isFoldable ? height / 2 : height;
    const borderRadius = shape === 'rounded' ? '20px' : '0px';

    const isShimmerType = envelopeType && SHIMMER_TYPES.includes(envelopeType);

    const serverStyle = {
      position: 'relative',
      top: '0pt',
      width: `${width}pt`,
      height: `${finalHeight}pt`,
      borderRadius,
      zIndex: 2,
      marginLeft: '0px',
      backgroundColor:
        (FeatureFlags.get('colouredEnvelope') && !isShimmerType && backgroundColor) || 'none',
    };

    const cardLabelStyle = {
      borderBottomLeftRadius: borderRadius,
    };

    // iterate through elements and use values from customizationValues if
    // element is present in customizationValues. These values originate from redux-form
    // during the customization flow and have any changes made by user
    const elementsWithFormValues =
      elements &&
      elements.map(element => {
        if (customizationValues && customizationValues[element.uuid]) {
          return customizationValues[element.uuid];
        }
        return element;
      });

    // partition text elements and image elements since we handle them differently
    const [textElements, imageElements] = _partition(
      elementsWithFormValues,
      element => element.content_type === 'TEXT'
    );

    // we need to find if there are any image elements that have a higher layer than text elements
    // because we articially increase the z-index of text elements according to their y
    // position, so we need to know about any images that are meant to be rendered on top of
    // text so we can increase their z-index so it will be rendered on top of all text elements

    let maxTextElementLayer;
    const maxTextElementByLayer = _maxBy(textElements, 'layer');
    if (maxTextElementByLayer) {
      maxTextElementLayer = maxTextElementByLayer.layer;
    }

    // update the layer property (used for z-index style) of any images that overlay all
    // text elements, this usually occurs with foil cards, but also a few others
    // NOTE: important for us to only do this if there are textElements, if there are
    // no text elements (maxTextElementLayer is undefined) then we just use imageElements unmodified
    const imageElementsWithAdjustedLayer = maxTextElementLayer
      ? imageElements.map(imageElement => {
          if (imageElement.layer > maxTextElementLayer) {
            return { ...imageElement, layer: finalHeight * 2 };
          }
          return imageElement;
        })
      : imageElements;

    // Image elements with a linked_element_uuid are assumed to be rsvp/meal option bullet points. If the associated text is deleted, hide the image.
    // Images with excluded_from_printing === true won't be printed, so shouldn't be rendered in the preview.
    const visibleImageElements = imageElementsWithAdjustedLayer.filter(element => {
      const {
        linked_element_uuid: linkedElementUUID,
        excluded_from_printing: excludedFromPrinting,
        overlay,
      } = element;
      const isHiddenOverlay = !!overlay && excludedFromPrinting;
      const hasEmptyLinkedElement =
        linkedElementUUID &&
        customizationValues &&
        customizationValues[linkedElementUUID] &&
        !customizationValues[linkedElementUUID].body;

      return !hasEmptyLinkedElement && !isHiddenOverlay;
    });

    // flag set on the backend for a page which has had its elements line heights adjusted
    // for a rendering improvement, this is required for backwards compatibility
    const boundingBoxesAdjusted = _indexOf(tags, 'bounding_boxes_adjusted') !== -1;

    return (
      <div
        className="customization-preview"
        style={{ width: `${width}pt`, height: `${finalHeight}pt` }}
        ref={this.setWrapperRef}
      >
        {children}
        <div
          className={cx('card', activeMenuName, {
            'semi-disable': customizationDeleted,
            hoverable: onClickAction,
            [envelopeType]: isShimmerType,
          })}
          style={serverStyle}
          onClick={onClickAction}
          ref={node => {
            this.cardRef = node;
          }}
        >
          {!disableBleed && (
            <Bleed
              width={width}
              height={finalHeight}
              verticalBleed={verticalBleed}
              horizontalBleed={horizontalBleed}
              isRounded={shape === 'rounded'}
            />
          )}
          {/* IMPORTANT: Wrap image layers in a full-sized div with 'overflow: hidden' so they don't overflow on cards with rounded corners */}
          {visibleImageElements.length > 0 && (
            <DragAndDrop>
              <div
                className="image-layer-wrapper"
                style={{
                  position: 'absolute',
                  width: `${width}pt`,
                  height: `${isFoldable ? height / 2 : height}pt`,
                  borderRadius,
                }}
              >
                {visibleImageElements.map(imageElement => (
                  <ImageLayer
                    key={imageElement.uuid}
                    customizationDeleted={customizationDeleted}
                    imageElement={imageElement}
                    isActive={activeField === imageElement.uuid}
                    controlsEnabled={
                      editable &&
                      hasMultipleCustomizableImages &&
                      imageElement.customizable &&
                      imageUploadEnabled
                    }
                    hasUploadcareImage={
                      !!cachedMetadata[imageElement.image_id] &&
                      !!cachedMetadata[imageElement.image_id].uploadcare_url
                    }
                    allowPlaceholder={editable && imageElement.customizable && imageUploadEnabled}
                    isFoldable={isFoldable}
                    isFlipped={isFlipped}
                    onImageDelete={onImageDelete}
                    onImageSave={onImageSave}
                    onImageReorder={this.handleImageReorder}
                    pageHeight={height}
                  />
                ))}
              </div>
            </DragAndDrop>
          )}
          {textElements.map((textElement, index) => (
            <DraggableTextLayer
              key={textElement.uuid}
              index={index}
              customizationValues={customizationValues}
              editable={editable && textElement.customizable}
              textElement={textElement}
              parentDimensions={{ width, height: finalHeight }}
              isActive={activeField === textElement.uuid}
              customizationDeleted={customizationDeleted}
              boundingBoxesAdjusted={boundingBoxesAdjusted}
              onSetFieldWidthError={onSetFieldWidthError}
              onRemoveFieldWidthError={onRemoveFieldWidthError}
              onSetElementHeightError={onSetElementHeightError}
              onRemoveElementHeightError={onRemoveElementHeightError}
              onUpdateField={onUpdateField}
              handleChange={handleChange}
              handleTextBlur={handleTextBlur}
              handleTextChange={handleTextChange}
              isFoldable={isFoldable}
              enableEnforceCaps={enableEnforceCaps && textElement.tag === 'RECIPIENT_ADDRESS'}
              customizationFoilColor={customizationFoilColor}
            />
          ))}
          {onClickAction && (
            <div className="actions-layer">
              <div className="action action--edit">
                <div className="action__icon">
                  <i className="zolaicon zolaicon-edit" />
                </div>
              </div>
            </div>
          )}
          {!_isEmpty(infoComponents) && (
            <div className="info-layer">
              {infoComponents.map((infoComponent, index) => (
                <div className="info-component-container" key={index}>
                  {infoComponent}
                </div>
              ))}
            </div>
          )}
          {footerLabel && (
            <div className="card-label-container" style={cardLabelStyle}>
              {footerLabel}
            </div>
          )}
        </div>
        {isFoldable && !isReviewStep && <div className="fold" />}
        {activeField &&
          customizationValues &&
          customizationValues[activeField] &&
          customizationValues[activeField].content_type === 'TEXT' && (
            <ElementStyleEditor
              element={customizationValues[activeField]}
              handleChange={handleChange}
              handleDelete={handleDelete}
              handleCloneElement={handleCloneElement} // experimental
              showFullMenu={
                // Flag for partial menus on envelope cards
                activeMenuName !== 'envelope' && activeMenuName !== 'rsvpEnvelope'
              }
              setActiveField={setActiveField}
              fonts={fonts}
              cardHeight={finalHeight}
              projectAvailableOptions={projectAvailableOptions}
              foiledElements={foiledElements}
              showFoilOnThisStep={showFoilOnThisStep}
              customizationCardType={customizationCardType}
              customizationFoilColor={customizationFoilColor}
              projectType={projectType}
              handleUpdateFoil={handleUpdateFoil}
              customizationSize={customizationSize}
            />
          )}
      </div>
    );
  }
}

Preview.propTypes = {
  page: PropTypes.shape({}),
  elements: PropTypes.arrayOf(PropTypes.shape({})),
  editable: PropTypes.bool,
  activeMenuName: PropTypes.string,
  children: PropTypes.element,
  customizationValues: PropTypes.shape({}),
  footerLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onSetFieldWidthError: PropTypes.func,
  onRemoveFieldWidthError: PropTypes.func,
  onSetElementHeightError: PropTypes.func,
  onRemoveElementHeightError: PropTypes.func,
  onImageDelete: PropTypes.func,
  onImageSave: PropTypes.func,
  setActiveField: PropTypes.func,
  shape: PropTypes.string,
  customizationDeleted: PropTypes.bool,
  disableBleed: PropTypes.bool,
  infoComponents: PropTypes.arrayOf(PropTypes.element),
  onClickAction: PropTypes.func,
  activeField: PropTypes.string,
  onUpdateField: PropTypes.func,
  handleChange: PropTypes.func,
  handleDelete: PropTypes.func,
  hasMultipleCustomizableImages: PropTypes.bool,
  imageUploadEnabled: PropTypes.bool,
  fonts: PropTypes.shape({
    mapped: PropTypes.arrayOf(PropTypes.shape({})),
    raw: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  handleTextBlur: PropTypes.func,
  handleTextChange: PropTypes.func,
  isFoldable: PropTypes.bool,
  enableEnforceCaps: PropTypes.bool,
  isFlipped: PropTypes.bool,
  isReviewStep: PropTypes.bool,
  handleCloneElement: PropTypes.func,
  projectAvailableOptions: PropTypes.shape({}),
  showFoilOnThisStep: PropTypes.bool,
  foiledElements: PropTypes.arrayOf(PropTypes.shape({})),
  customizationCardType: PropTypes.string,
  customizationFoilColor: PropTypes.string,
  projectType: PropTypes.string,
  handleUpdateFoil: PropTypes.func,
  customizationSize: PropTypes.string,
  backgroundColor: PropTypes.string,
  envelopeType: PropTypes.string,
};

const mapStateToProps = state => ({
  cachedMetadata: getCachedMetadata(state),
  cachedUploadcareFiles: getCachedUploadcareFiles(state),
});

const mapDispatchToProps = dispatch => {
  const setActiveField = name => dispatch(projectActions.setActiveField(name));
  return {
    setActiveField,
    copyImageToZola: (uploadcareFile, cache) =>
      dispatch(saveUploadcareImageToZola(uploadcareFile, cache)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Preview);



// WEBPACK FOOTER //
// ./src/cards/components/Customization/Preview/Preview.jsx